import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { searchPlayers, createPlayerInvitation } from '../services/clubs';
import './PlayerInviteModal.css';

// Interface for player search results
interface PlayerSearchResult {
  id: number;
  preName: string;
  name: string;
  email: string;
  pin: string;
}

interface PlayerInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: number;
  onPlayerInvited: () => void;
}

const PlayerInviteModal: React.FC<PlayerInviteModalProps> = ({
  isOpen,
  onClose,
  clubId,
  onPlayerInvited
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PlayerSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<'member' | 'organizer' | 'admin'>('member');
  const [invitationMessage, setInvitationMessage] = useState('');

  const searchForPlayers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await searchPlayers(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching players:', err);
      setError(err instanceof Error ? err.message : 'Failed to search players');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timeoutId = setTimeout(() => {
        searchForPlayers();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, searchForPlayers]);

  const handleInvitePlayer = async (player: PlayerSearchResult) => {
    setInviting(player.id);
    setError(null);
    try {
      await createPlayerInvitation(clubId, player.id, selectedRole, invitationMessage || undefined);
      onPlayerInvited();
      onClose();
      setSearchQuery('');
      setSearchResults([]);
      setInvitationMessage('');
    } catch (err) {
      console.error('Error inviting player:', err);
      setError(err instanceof Error ? err.message : 'Failed to invite player');
    } finally {
      setInviting(null);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content player-invite-modal">
        <div className="modal-header">
          <h3>{t('clubs.invitePlayer')}</h3>
          <button onClick={handleClose} className="close-button">&times;</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="searchQuery">{t('clubs.searchPlayers')}</label>
            <input
              type="text"
              id="searchQuery"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('clubs.searchPlayersPlaceholder')}
              className="search-input"
            />
            {loading && <div className="loading-spinner">Searching...</div>}
          </div>

          <div className="form-group">
            <label htmlFor="role">{t('clubs.inviteAs')}</label>
            <select
              id="role"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as 'member' | 'organizer' | 'admin')}
              className="role-select"
            >
              <option value="member">{t('clubs.roles.member')}</option>
              <option value="organizer">{t('clubs.roles.organizer')}</option>
              <option value="admin">{t('clubs.roles.admin')}</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="message">{t('clubs.invitationMessage')}</label>
            <textarea
              id="message"
              value={invitationMessage}
              onChange={(e) => setInvitationMessage(e.target.value)}
              placeholder={t('clubs.invitationMessagePlaceholder')}
              className="message-textarea"
              rows={3}
              maxLength={500}
            />
            <p className="help-text">{t('clubs.invitationMessageHelp')}</p>
          </div>

          {searchResults.length > 0 && (
            <div className="search-results">
              <h4>{t('clubs.searchResults')}</h4>
              <div className="player-list">
                {searchResults.map((player) => {
                  const isIncomplete = !player.preName?.trim() || !player.name?.trim() || !player.pin?.trim();
                  return (
                    <div key={player.id} className={`player-item ${isIncomplete ? 'incomplete-profile' : ''}`}>
                      <div className="player-info">
                        <div className="player-name">
                          {player.preName} {player.name}
                          {isIncomplete && (
                            <span className="incomplete-badge">
                              ⚠️ {t('clubs.incompleteProfile')}
                            </span>
                          )}
                        </div>
                        <div className="player-email">
                          {player.email}
                        </div>
                      </div>
                      <button
                        onClick={() => handleInvitePlayer(player)}
                        disabled={inviting === player.id || isIncomplete}
                        className={`btn btn-primary btn-small ${isIncomplete ? 'disabled' : ''}`}
                        title={isIncomplete ? t('clubs.cannotInviteIncomplete') : ''}
                      >
                        {inviting === player.id ? t('clubs.inviting') : t('clubs.invite')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {searchQuery.length >= 2 && searchResults.length === 0 && !loading && (
            <div className="no-results">
              {t('clubs.noPlayersFound')}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={handleClose} className="btn btn-secondary">
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerInviteModal;
